import { Component, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Brand } from "app/core/models/brand.model";
import { Company } from "app/core/models/company.model";
import { CurrentInstanceSelection } from "app/core/models/current-instance-selection.model";
import { Hotel } from "app/core/models/hotel.model";
import { InstanceActiveService } from "app/core/services/instance-active.service";
import { UserDetailsService } from "app/core/services/user-details.service";
import { filter, take } from "rxjs";

@UntilDestroy()
@Component({
    selector: "instance-selector",
    templateUrl: "./instance-selector.component.html",
    styleUrls: ["./instance-selector.component.scss"],
})
export class InstanceSelectorComponent implements OnInit {
    companies: Array<Company> = [];
    hotels: Array<Hotel> = [];
    hotelsFiltered: Array<Hotel> = [];
    brands: Array<Brand> = [];

    selectedCompany: Company;
    selectedHotel: Hotel;
    selectedBrand: Brand;

    companiesDropdownDisabled = false;
    brandsDropdownDisabled = false;
    hotelsDropdownDisabled = false;

    private syncingFromService = false;

    constructor(
        private _userDetailsService: UserDetailsService,
        private _instanceActiveService: InstanceActiveService
    ) {}

    ngOnInit(): void {
        this._instanceActiveService
            .findCompanies()
            .pipe(untilDestroyed(this), take(1))
            .subscribe((companies: Company[]) => {
                this.companies = (companies ?? []).sort((a, b) => a.name.localeCompare(b.name));
                this.companiesDropdownDisabled =
                    !this._userDetailsService.hasAnyPermission("ROLE_NOC") || this.companies.length === 1;

                const current = this._instanceActiveService.currentInstanceSelector;
                if (current) {
                    this.syncFromSelection(current);
                } else if (this.companies.length === 1) {
                    this.selectedCompany = this.companies[0];
                    this.onCompanyChange(true);
                }
            });

        this._instanceActiveService.currentInstanceSelector$
            .pipe(
                untilDestroyed(this),
                filter((selection): selection is CurrentInstanceSelection => !!selection)
            )
            .subscribe(selection => this.syncFromSelection(selection));
    }

    onCompanyChange(saveContext: boolean = true): void {
        this.selectedBrand = null;
        this.selectedHotel = null;
        this.brands = [];
        this.hotels = [];
        this.hotelsFiltered = [];

        if (saveContext && !this.syncingFromService) {
            this._instanceActiveService.currentCompany = this.selectedCompany;
        }

        if (!this.selectedCompany) {
            return;
        }

        this._instanceActiveService
            .findBrandsByIdCompany(this.selectedCompany.id)
            .pipe(untilDestroyed(this), take(1))
            .subscribe(data => {
                this.brands = (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
            });
    }

    onBrandChange(saveContext: boolean = true): void {
        this.selectedHotel = null;
        this.hotels = [];
        this.hotelsFiltered = [];

        if (saveContext && !this.syncingFromService) {
            this._instanceActiveService.currentBrand = this.selectedBrand;
        }

        if (!this.selectedBrand) {
            return;
        }

        this._instanceActiveService
            .findHotelsByIdBrand(this.selectedBrand.id)
            .pipe(untilDestroyed(this), take(1))
            .subscribe(data => {
                const hotels = (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
                this.hotels = hotels;
                this.hotelsFiltered = [...hotels];
            });
    }

    onHotelChange(saveContext: boolean = true): void {
        if (saveContext && !this.syncingFromService) {
            this._instanceActiveService.currentHotel = this.selectedHotel;
        }
    }

    selectCompany(company: Company, saveContext: boolean = true): void {
        this.selectedCompany = company;
        this.onCompanyChange(saveContext);
    }

    selectBrand(brand: Brand, saveContext: boolean = true): void {
        this.selectedBrand = brand;
        this.onBrandChange(saveContext);
    }

    selectHotel(hotel: Hotel, saveContext: boolean = true): void {
        this.selectedHotel = hotel;
        this.onHotelChange(saveContext);
    }

    compareEntities(e1: any, e2: any): boolean {
        if ((!e1 && e2) || (e1 && !e2)) {
            return false;
        }
        return e1?.id === e2?.id;
    }

    private syncFromSelection(selection: CurrentInstanceSelection): void {
        const companyId = selection.company?.id;
        const brandId = selection.brand?.id;
        const hotelId = selection.hotel?.id;

        const sameCompany = this.selectedCompany?.id === companyId;
        const sameBrand = this.selectedBrand?.id === brandId;
        const sameHotel = this.selectedHotel?.id === hotelId;

        if (sameCompany && sameBrand && sameHotel && this.hotels.length > 0) {
            return;
        }

        this.syncingFromService = true;

        if (!sameCompany || this.companies.length === 0) {
            this.selectedCompany = this.findById(this.companies, companyId) ?? selection.company;
        }

        if (!this.selectedCompany?.id) {
            this.syncingFromService = false;
            return;
        }

        this._instanceActiveService
            .findBrandsByIdCompany(this.selectedCompany.id)
            .pipe(untilDestroyed(this), take(1))
            .subscribe(brands => {
                this.brands = (brands ?? []).sort((a, b) => a.name.localeCompare(b.name));
                this.selectedBrand = this.findById(this.brands, brandId) ?? selection.brand ?? null;

                if (!this.selectedBrand?.id) {
                    this.hotels = [];
                    this.hotelsFiltered = [];
                    this.selectedHotel = null;
                    this.syncingFromService = false;
                    return;
                }

                this._instanceActiveService
                    .findHotelsByIdBrand(this.selectedBrand.id)
                    .pipe(untilDestroyed(this), take(1))
                    .subscribe(hotels => {
                        const sorted = (hotels ?? []).sort((a, b) => a.name.localeCompare(b.name));
                        this.hotels = sorted;
                        this.hotelsFiltered = [...sorted];
                        this.selectedHotel = this.findById(this.hotels, hotelId) ?? null;
                        this.syncingFromService = false;
                    });
            });
    }

    private findById<T extends { id: number }>(items: T[], id: number | undefined): T | undefined {
        if (id == null || !items?.length) {
            return undefined;
        }
        return items.find(item => item.id === id);
    }
}
